import React from 'react';
import { usePreviewContext } from '../providers/PreviewProvider';

export default function BoostProgress() {
  const { boostProgress } = usePreviewContext();

  if (!boostProgress) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(18px)' }} />
      {/* Ambient orbs */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 70%)', top: '20%', left: '20%', filter: 'blur(50px)', animation: 'bOrb1 7s ease-in-out infinite alternate' }} />
        <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(234,88,12,0.1) 0%, transparent 70%)', bottom: '25%', right: '20%', filter: 'blur(40px)', animation: 'bOrb2 9s ease-in-out infinite alternate' }} />
      </div>

      <div style={{ position: 'relative', width: '100%', maxWidth: 460, borderRadius: 24, overflow: 'hidden', background: 'linear-gradient(145deg, #09090b 0%, #1a0f05 55%, #09090b 100%)', border: '1px solid rgba(249,115,22,0.3)', boxShadow: '0 0 100px rgba(249,115,22,0.18), 0 0 40px rgba(234,88,12,0.12), 0 40px 80px rgba(0,0,0,0.9)' }}>

        {/* Animated gradient top border */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent 0%, #EA580C 25%, #FB923C 50%, #F97316 75%, transparent 100%)', backgroundSize: '200% 100%', animation: 'bShimmer 2.5s linear infinite' }} />

        {/* Header */}
        <div style={{ padding: '22px 24px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #F97316, #EA580C)', display: 'flex', alignItems: 'center', justify: 'center', fontSize: 22, flexShrink: 0, boxShadow: '0 0 28px rgba(249,115,22,0.6)', animation: 'bPulse 2.2s ease-in-out infinite' }}>✨</div>
            <div style={{ flex: 1 }}>
              <h2 style={{ color: '#f1f5f9', fontWeight: 800, fontSize: 17, margin: 0, letterSpacing: '-0.02em' }}>
                AI Boost in Progress
              </h2>
              <p style={{ color: boostProgress.done ? '#34d399' : '#FB923C', fontSize: 11, margin: '4px 0 0', fontWeight: 600, transition: 'color 0.4s' }}>
                {boostProgress.done
                  ? '✅ All sections optimized!'
                  : boostProgress.totalSteps > 0
                    ? `Step ${boostProgress.currentStep} of ${boostProgress.totalSteps} · Optimizing...`
                    : 'Initializing...'}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: 16, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3,
              background: boostProgress.done
                ? 'linear-gradient(90deg, #10b981, #34d399)'
                : 'linear-gradient(90deg, #EA580C, #F97316, #FB923C)',
              backgroundSize: '200% 100%',
              width: `${boostProgress.totalSteps > 0 ? Math.round((boostProgress.currentStep / boostProgress.totalSteps) * 100) : 5}%`,
              transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1), background 0.6s ease',
              boxShadow: boostProgress.done ? '0 0 14px rgba(16,185,129,0.7)' : '0 0 14px rgba(249,115,22,0.7)',
              animation: !boostProgress.done ? 'bBarFlow 2s linear infinite' : 'none',
            }} />
          </div>
          <div style={{ display: 'flex', justify: 'space-between', marginTop: 5 }}>
            <span style={{ fontSize: 9.5, color: '#334155', fontWeight: 500 }}>
              {boostProgress.done ? 'Complete' : 'Resume preview updating live...'}
            </span>
            <span style={{ fontSize: 10, color: boostProgress.done ? '#34d399' : '#F97316', fontWeight: 800, transition: 'color 0.4s' }}>
              {boostProgress.totalSteps > 0 ? Math.round((boostProgress.currentStep / boostProgress.totalSteps) * 100) : 0}%
            </span>
          </div>
        </div>

        {/* Steps list */}
        <div style={{ padding: '2px 20px 16px', display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 340, overflowY: 'auto' }}>
          {boostProgress.steps.map((step) => {
            const isDone = step.status === 'done';
            const isActive = step.status === 'active';
            const isError = step.status === 'error';
            const isPend = step.status === 'pending';
            return (
              <div key={step.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 11,
                background: isActive ? 'rgba(249,115,22,0.12)' : isDone ? 'rgba(16,185,129,0.06)' : isError ? 'rgba(239,68,68,0.06)' : 'rgba(255,255,255,0.025)',
                border: `1px solid ${isActive ? 'rgba(249,115,22,0.35)' : isDone ? 'rgba(16,185,129,0.15)' : isError ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)'}`,
                transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
                transform: isActive ? 'translateX(3px)' : 'translateX(0)',
              }}>
                {/* Status indicator */}
                <div style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justify: 'center', flexShrink: 0 }}>
                  {isDone ? (
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(16,185,129,0.18)', border: '1.5px solid rgba(16,185,129,0.45)', display: 'flex', alignItems: 'center', justify: 'center' }}>
                      <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>
                  ) : isActive ? (
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2.5px solid rgba(249,115,22,0.25)', borderTopColor: '#F97316', animation: 'bSpin 0.7s linear infinite' }} />
                  ) : isError ? (
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '1.5px solid rgba(239,68,68,0.35)', display: 'flex', alignItems: 'center', justify: 'center', fontSize: 10, color: '#ef4444' }}>✕</div>
                  ) : (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                  )}
                </div>
                {/* Emoji icon */}
                <span style={{ fontSize: 15, opacity: isPend ? 0.25 : 1, transition: 'opacity 0.35s', flexShrink: 0 }}>{step.icon}</span>
                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 12.5, fontWeight: isActive ? 700 : isDone ? 500 : 400, color: isActive ? '#FB923C' : isDone ? '#6ee7b7' : isError ? '#fca5a5' : '#a1a1aa', display: 'block', transition: 'all 0.35s' }}>
                    {step.label}
                  </span>
                  {isActive && (
                    <span style={{ fontSize: 10, color: '#F97316', display: 'block', marginTop: 2, animation: 'bFadeIn 0.3s ease', fontWeight: 500 }}>Working on this section...</span>
                  )}
                  {isDone && (
                    <span style={{ fontSize: 10, color: '#10b981', display: 'block', marginTop: 2, animation: 'bFadeIn 0.3s ease' }}>Done ✓</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.12)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#F97316', flexShrink: 0, animation: 'bDot 1.3s ease-in-out infinite' }} />
            <p style={{ fontSize: 10.5, color: '#a1a1aa', margin: 0, lineHeight: 1.5 }}>
              Resume preview is updating <strong style={{ color: '#FB923C' }}>live</strong> as each section is optimized.
              <span style={{ color: '#71717a' }}> Do not close this window.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
